import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {Body2, Caption, Display3, Headline1} from '@adv-mt/ui/typography'

import {BASE_CLASS} from './config.js'

const getVehicleTypeLabel = ({config, hasStock, i18n, isRentingAd, offerTypeId, subscriptionVehicleState}) => {
  const {OFFER_TYPE_DEMONSTRATION, OFFER_TYPE_KM0, OFFER_TYPE_MANAGEMENT, OFFER_TYPE_NEW, OFFER_TYPE_SUBSTITUTION} =
    config.get('search')
  const {VEHICLE_STATE_IDS} = config.get('subscription')

  if (isRentingAd) {
    return subscriptionVehicleState === VEHICLE_STATE_IDS.NEW
      ? i18n.t('COMMON.DETAIL_VEHICLE_TYPE.RENTING_NEW')
      : i18n.t('COMMON.DETAIL_VEHICLE_TYPE.RENTING_SECOND_HAND')
  }

  if (offerTypeId === OFFER_TYPE_KM0) return i18n.t('COMMON.DETAIL_VEHICLE_TYPE.KM0')
  if (offerTypeId === OFFER_TYPE_SUBSTITUTION) return i18n.t('COMMON.DETAIL_VEHICLE_TYPE.SUBSTITUTION')
  if (offerTypeId === OFFER_TYPE_MANAGEMENT) return i18n.t('COMMON.DETAIL_VEHICLE_TYPE.MANAGEMENT')
  if (offerTypeId === OFFER_TYPE_DEMONSTRATION) return i18n.t('COMMON.DETAIL_VEHICLE_TYPE.DEMONSTRATION')

  if (offerTypeId === OFFER_TYPE_NEW) {
    return hasStock ? i18n.t('COMMON.DETAIL_VEHICLE_TYPE.NEW_IN_STOCK') : i18n.t('COMMON.DETAIL_VEHICLE_TYPE.NEW')
  }

  return i18n.t('COMMON.DETAIL_VEHICLE_TYPE.SECOND_HAND')
}

const Heading = ({ad = {}, isRentingAd}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')

  const {creationDate, hasStock, offerType, publicationDate, subscriptionVehicleState, title} = ad

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !config.get('isMobile')
  })

  const Title = isHigherThanMobile ? Display3 : Headline1
  const Eyebrow = isHigherThanMobile ? Body2 : Caption

  // The ad is first published when it is created, so its latest publicationDate is what reads as "modified"
  const publishedDateFormats = useDateFormat(new Date(creationDate))
  const modifiedDateFormats = useDateFormat(new Date(publicationDate))

  const vehicleTypeLabel = getVehicleTypeLabel({
    config,
    hasStock,
    i18n,
    isRentingAd,
    offerTypeId: offerType?.id,
    subscriptionVehicleState
  })

  return (
    <div className={`${BASE_CLASS}-heading`}>
      <div className={`${BASE_CLASS}-eyebrow`}>
        <Eyebrow as="p" highlight>
          {vehicleTypeLabel}
        </Eyebrow>
        <span className={`${BASE_CLASS}-eyebrowSeparator`}>·</span>
        <div className={`${BASE_CLASS}-eyebrowDates`}>
          <Eyebrow as="p">
            {i18n.t('COMMON.DETAIL_DATE_PUBLISHED_REVAMP', {
              publishedDate: `${publishedDateFormats.numericShort} ${publishedDateFormats.hour}`
            })}
          </Eyebrow>
          <span className={`${BASE_CLASS}-eyebrowDatesSeparator`}>·</span>
          <Eyebrow as="p">
            {i18n.t('COMMON.DETAIL_DATE_MODIFIED_REVAMP', {
              modifiedDate: `${modifiedDateFormats.numericShort} ${modifiedDateFormats.hour}`
            })}
          </Eyebrow>
        </div>
      </div>
      <Title as="h1" className={`${BASE_CLASS}-title`}>
        {title}
      </Title>
    </div>
  )
}

Heading.displayName = 'Heading'
Heading.propTypes = {
  ad: PropTypes.object,
  isRentingAd: PropTypes.bool
}

export default Heading
